import { useEffect } from "react";
import { motion } from "framer-motion";

const SPARKS = Array.from({ length: 18 }, (_, i) => ({
  id: i,
  angle: (i / 18) * Math.PI * 2,
  dist: 120 + (i % 3) * 55,
  delay: 0.5 + (i % 5) * 0.09,
  size: 5 + (i % 3) * 4,
}));

export const TierCelebration = ({ tier, onClose }) => {
  useEffect(() => {
    const t = setTimeout(onClose, 4500);
    return () => clearTimeout(t);
  }, [onClose]);

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} onClick={onClose}
      data-testid="tier-celebration"
      className="fixed inset-0 z-[100] flex flex-col items-center justify-center overflow-hidden cursor-pointer"
      style={{ background: `radial-gradient(circle at 50% 42%, ${tier.color}30, #05060B 78%)` }}>
      <motion.div animate={{ rotate: 360 }} transition={{ repeat: Infinity, duration: 16, ease: "linear" }}
        className="absolute w-[150vmax] h-[150vmax] pointer-events-none"
        style={{
          background: "repeating-conic-gradient(from 0deg, rgba(253,224,139,0.14) 0deg 10deg, transparent 10deg 24deg)",
          maskImage: "radial-gradient(circle, black 0%, transparent 55%)",
          WebkitMaskImage: "radial-gradient(circle, black 0%, transparent 55%)",
        }} />

      <div className="relative flex items-center justify-center">
        <motion.span initial={{ scale: 0, opacity: 0 }} animate={{ scale: [0, 2.6], opacity: [0.9, 0] }}
          transition={{ delay: 0.35, duration: 1.1, ease: "easeOut" }}
          className="absolute w-40 h-40 rounded-full border-2 pointer-events-none" style={{ borderColor: tier.color }} />
        {SPARKS.map((s) => (
          <motion.span key={s.id} initial={{ x: 0, y: 0, scale: 0, opacity: 1 }}
            animate={{ x: Math.cos(s.angle) * s.dist, y: Math.sin(s.angle) * s.dist, scale: [0, 1.2, 0], opacity: [1, 1, 0] }}
            transition={{ delay: s.delay, duration: 1.3, ease: "easeOut" }}
            className="absolute rounded-full pointer-events-none"
            style={{ width: s.size, height: s.size, background: s.id % 2 ? "#FDE08B" : tier.color, boxShadow: `0 0 8px ${tier.color}` }} />
        ))}
        {tier.image ? (
          <motion.img src={tier.image} alt={tier.name} data-testid="tier-celebration-badge"
            initial={{ scale: 0, rotate: -18 }} animate={{ scale: [0, 1.22, 1], rotate: 0 }}
            transition={{ delay: 0.15, duration: 0.9, type: "spring", bounce: 0.45 }}
            className="w-44 h-44 object-contain"
            style={{ filter: `drop-shadow(0 0 28px ${tier.color}AA) drop-shadow(0 6px 14px rgba(0,0,0,0.6))` }} />
        ) : (
          <motion.span initial={{ scale: 0 }} animate={{ scale: 1 }} className="text-8xl">{tier.emoji}</motion.span>
        )}
      </div>

      <motion.p initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.7 }}
        className="mt-7 text-[11px] font-extrabold tracking-[0.35em] text-[#FDE08B]">⚜ LEVEL UP ⚜</motion.p>
      <motion.p initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.85 }}
        data-testid="tier-celebration-name"
        className="mt-1.5 text-4xl font-extrabold font-display" style={{ color: tier.color, textShadow: `0 0 26px ${tier.color}88` }}>
        {tier.name}
      </motion.p>
      <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.05 }}
        className="mt-2 text-zinc-400 text-xs">Your royal status is active for 30 days</motion.p>
      <motion.p initial={{ opacity: 0 }} animate={{ opacity: [0, 1, 0.4, 1] }} transition={{ delay: 1.6, duration: 2.4, repeat: Infinity }}
        className="absolute bottom-12 text-zinc-500 text-[10px]">Tap anywhere to continue</motion.p>
    </motion.div>
  );
};
